/** @jsx jsx */

import React, { useEffect } from "react";
import { Button, Card, Col, Divider, Row, Typography } from "antd";
import { jsx } from "@emotion/core";

import CustomButton from "../../../components/button";
import BillsList from "../../bills/billList/BillsList";
import useLoanStatus from "../../../hooks/auth/useLoanStatus";
import useRedirect from "../../../helperHooks/useRedirect";
import useAPI from "../../../helperHooks/useAPI";

import { BillService } from "../../../services/billService/index";
import { color } from "../../../theme/Theme";
import { billsCardContents as CONSTANTS } from "./BillsCardConstants";
import { billsCardStyles, billsMetaDataStyle } from "./BillsCardStyles";

const { Text } = Typography;

interface HomePageCardProps {
  preventDataFetch: boolean;
}

const BillsCard: React.FC<HomePageCardProps> = ({
  preventDataFetch
}: HomePageCardProps) => {
  const redirect = useRedirect();
  const { disableBillsPage } = useLoanStatus();

  const { callAPI: getBillList, ...billListData } = useAPI(
    BillService.getBills
  );
  const { loading, response } = billListData;

  useEffect(() => {
    if (!preventDataFetch) {
      getBillList({});
    }
  }, [getBillList, preventDataFetch]);

  const pendingBillsCount = response?.noOfPending || 0;
  const rejectedBillsCount = response?.noOfRejected || 0;
  const paidBillsCount =
    (response?.noOfPaidWithReceipt || 0) +
    (response?.noOfPaidWithoutReceipt || 0);

  const getClassName = (className?: string) =>
    `${className} ${disableBillsPage ? "disabled-text" : ""}`;

  const renderBillsMetaData = (
    label: string,
    count: number,
    circleColor: string
  ) => (
    <section className="d-flex flex-column align-items-center flex-grow-1">
      <Text className={getClassName("metadata-label")}>{label}</Text>
      <span
        css={billsMetaDataStyle(
          !disableBillsPage ? circleColor : color.disabledBackground
        )}
        className={getClassName(
          "px-3 mt-3 d-flex justify-content-center align-items-center"
        )}
      >
        {count}
      </span>
    </section>
  );

  const handleSeeMoreClick = () => {
    redirect(routes => routes.bills.new);
  };

  const handleUtilizeMoneyClick = () => {
    redirect(routes => routes.bills.upload);
  };

  return (
    <Card bordered={false} loading={loading} css={billsCardStyles}>
      <Row type="flex" className="justify-content-between align-items-center">
        <Text className={getClassName("home-card-title")}>
          {CONSTANTS.labels.bills}
        </Text>
        <CustomButton
          type="primary"
          size="small"
          disabled={disableBillsPage}
          onClick={handleUtilizeMoneyClick}
        >
          {CONSTANTS.buttons.utilizeMoney}
        </CustomButton>
      </Row>
      <Row type="flex" className="pt-5 pb-3">
        <Col xs={24} lg={8} className="d-flex justify-content-center">
          {renderBillsMetaData(
            CONSTANTS.labels.pending,
            pendingBillsCount,
            color.lightYellow
          )}
        </Col>
        <Col xs={24} lg={8} className="d-flex justify-content-center">
          <Divider type="vertical" dashed className="d-none d-lg-block" />
          {renderBillsMetaData(
            CONSTANTS.labels.rejected,
            rejectedBillsCount,
            color.melon
          )}
          <Divider type="vertical" dashed className="d-none d-lg-block" />
        </Col>
        <Col xs={24} lg={8} className="d-flex justify-content-center">
          {renderBillsMetaData(
            CONSTANTS.labels.paid,
            paidBillsCount,
            color.lightGreen
          )}
        </Col>
      </Row>
      {!disableBillsPage && (
        <React.Fragment>
          <Text className={getClassName("d-block table-title mb-3 mt-1")}>
            {CONSTANTS.labels.recentBills}
          </Text>
          <BillsList
            inBillsCard
            className={`${!disableBillsPage ? "" : "disabled-table"}`}
            billsListResponse={response}
          />
          {(response?.challanDetailsSet || []).length > 3 && (
            <Row type="flex" justify="center" className="pt-3">
              <Button
                type="link"
                disabled={disableBillsPage}
                onClick={handleSeeMoreClick}
              >
                {CONSTANTS.buttons.seeMore}
              </Button>
            </Row>
          )}
        </React.Fragment>
      )}
    </Card>
  );
};

export default BillsCard;
